import axios from 'axios'

const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL || ''}/api`,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

// Attach admin token on every request if present
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('adminToken')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Global error handler
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      sessionStorage.removeItem('adminToken')
      window.location.href = '/admin/login'
    }
    return Promise.reject(err)
  }
)

// ── Case ID ─────────────────────────────────────────────────────
export const validateCaseId = (caseId, viewerPin) =>
  api.post('/cases/validate', { caseId, viewerPin })

export const checkCaseId = (caseId) =>
  api.post('/cases/check', { caseId })

export const getActiveMeeting = (caseId, viewerPin) =>
  api.post(`/cases/${caseId}/active-meeting`, { viewerPin })

// ── Upload ──────────────────────────────────────────────────────
export const initiateUpload = (caseId, filename, fileSize, validationStatus, validationReport, sessionFolder, subfolderPath) =>
  api.post('/upload/initiate', { caseId, filename, fileSize, validationStatus, validationReport, sessionFolder, subfolderPath })

// All files upload directly to Google Drive (browser → Drive).
// Backend only creates the upload session and confirms completion.
// Zero Cloud Run bandwidth for file bytes.
export const uploadFile = async (uploadId, file, onProgress, signal) => {
  // Step 1: Get a resumable upload URL from the backend
  const { data: { uploadUrl } } = await api.post(
    `/upload/start/${uploadId}`,
    { fileSize: file.size },
  )

  // Step 2: Upload file directly to Drive using XMLHttpRequest for progress.
  // No Authorization header: the resumable URL is itself the grant.
  const driveFileId = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', uploadUrl, true)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')

    let aborted = false

    xhr.upload.onprogress = (e) => {
      if (!aborted && onProgress && e.lengthComputable) onProgress(e.loaded, e.total)
    }

    xhr.onload = () => {
      if (aborted) return
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const resp = JSON.parse(xhr.responseText)
          resolve(resp.id)
        } catch {
          reject(new Error('Invalid response from Drive'))
        }
      } else {
        reject(new Error(`Drive upload failed: ${xhr.status} ${xhr.statusText}`))
      }
    }

    xhr.onerror = () => { if (!aborted) reject(new Error('Network error during Drive upload')) }
    xhr.ontimeout = () => reject(new Error('Drive upload timed out'))
    xhr.onabort = () => { aborted = true; reject(new Error('Upload aborted')) }

    if (signal) {
      signal.addEventListener('abort', () => xhr.abort())
    }

    xhr.send(file)
  })

  // Step 3: Tell the backend the file is on Drive
  return api.post(`/upload/complete/${uploadId}`, {
    driveFileId,
    fileSize: file.size,
  })
}

// ── Batch upload (optimized — 2 backend calls total) ────────────
export const batchInitUpload = (caseId, sessionFolder, files, validationStatus, validationReport) =>
  api.post('/upload/batch-init', { caseId, sessionFolder, files, validationStatus, validationReport }, { timeout: 300000 })

// 300s timeout: recording a large session writes one row per file inside a
// transaction and can outlast the default 30s. That used to be harmless — the
// call was fire-and-forget, so a client timeout was ignored while the server
// finished anyway. It is not harmless now that the call is retried: a timeout
// would fire a second delivery while the first is still running, and two
// concurrent deliveries both count as new (doubling uploadCount and sending a
// duplicate notification). Not timing out is what keeps deliveries sequential.
export const batchCompleteUpload = (caseId, sessionFolder, validationStatus, results) =>
  api.post('/upload/batch-complete', { caseId, sessionFolder, validationStatus, results }, { timeout: 300000 })

// Absolute URL for the same endpoint. sendBeacon cannot go through the axios
// instance, so the unload flush needs the resolved path.
export const batchCompleteUrl = () =>
  `${import.meta.env.VITE_API_BASE_URL || ''}/api/upload/batch-complete`

// Tells the backend a session was cancelled so its unrecorded rows are removed
// rather than being reported as a failed upload two hours later.
export const discardUploadSession = (caseId, uploadIds) =>
  api.post('/upload/batch-discard', { caseId, uploadIds })

// Upload a single file directly to Drive using a pre-created resumable upload URL.
// The backend creates the resumable session; the browser PUTs file bytes directly.
// No Authorization header: the resumable URL is itself the grant (same as Occlusa's putToDrive).
export const uploadFileDirect = (uploadUrl, file, onProgress, signal) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', uploadUrl, true)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')

    let aborted = false

    xhr.upload.onprogress = (e) => {
      if (!aborted && onProgress && e.lengthComputable) onProgress(e.loaded, e.total)
    }

    xhr.onload = () => {
      if (aborted) return
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const resp = JSON.parse(xhr.responseText)
          resolve(resp.id) // driveFileId
        } catch {
          reject(new Error('Invalid response from Drive'))
        }
      } else {
        reject(new Error(`Drive upload failed: ${xhr.status} ${xhr.statusText}`))
      }
    }

    xhr.onerror = () => { if (!aborted) reject(new Error('Network error during Drive upload')) }
    xhr.ontimeout = () => reject(new Error('Drive upload timed out'))
    xhr.onabort = () => { aborted = true; reject(new Error('Upload aborted')) }

    if (signal) {
      signal.addEventListener('abort', () => xhr.abort())
    }

    xhr.send(file)
  })
}

// Returns all uploads + validation reports for a case (public — no auth needed)
export const getCaseReport = (caseId) =>
  api.get(`/upload/report/${caseId}`)

// ── Admin — Auth ─────────────────────────────────────────────────
export const adminGoogleCallback = (code) =>
  api.post('/admin/auth/callback', { code })

export const adminLogout = () =>
  api.post('/admin/auth/logout')

// ── Admin — Cases ────────────────────────────────────────────────
export const getCases = (params) =>
  api.get('/admin/cases', { params })

// ── Tracker (READ-ONLY mirror of the O3D Tracker Google Sheet — the
//    sheet is the source of truth; the portal never writes tracker data) ──
export const getTrackerCases = (params) =>
  api.get('/tracker/cases', { params })

export const getTrackerSummary = () =>
  api.get('/tracker/summary')

export const getTrackerReport = (params) =>
  api.get('/tracker/reports', { params })

export const getTrackerReportStats = (params) =>
  api.get('/tracker/reports/stats', { params })

export const getTrackerCase = (id) =>
  api.get(`/tracker/cases/${id}`)

export const getTrackerStandup = () =>
  api.get('/tracker/standup') // the active-cases standup board

export const getTrackerPrinters = () =>
  api.get('/tracker/printers') // print-queue + printer fleet view

export const syncTracker = () =>
  // sync fetches the whole sheet + rebuilds the cache — allow well past the
  // default 30s client timeout (server transaction budget is 120s).
  // Body must be {} — axios serializes null to a literal "null" body, which
  // express's strict JSON parser rejects with "null is not valid JSON".
  api.post('/tracker/sync', {}, { timeout: 150000 })

export const getTrackerSyncStatus = () =>
  api.get('/tracker/sync/status')

export const getTrackerRows = (params) =>
  api.get('/tracker/rows', { params }) // sheet-view: one row per sheet row

export const getTrackerRowTabs = () =>
  api.get('/tracker/rows/tabs')

export const getTrackerOpsStandup = () =>
  api.get('/tracker/ops/standup')

export const getTrackerOpsOverview = () =>
  api.get('/tracker/ops/overview')

export const getTrackerRefList = (view, params) =>
  api.get(`/tracker/${view}`, { params }) // printer-log | print-info | printer-service | case-info | doctors

export const getTrackerGrid = (tab) =>
  api.get(`/tracker/grids/${encodeURIComponent(tab)}`)

export const createCase = (notes, caseId) =>
  api.post('/admin/cases', { notes, ...(caseId ? { caseId } : {}) })

export const updateCase = (oldCaseId, data) =>
  api.patch(`/admin/cases/${oldCaseId}`, data)

export const toggleCaseStatus = (caseId) =>
  api.patch(`/admin/cases/${caseId}/toggle`)

export const toggleCaseDone = (caseId) =>
  api.patch(`/admin/cases/${caseId}/done`)

// Deletes cascade across related tables + best-effort Google Drive cleanup,
// which can take a while for cases with many files — give it a generous budget
// (default 30s client timeout would abort while the server is still working).
export const deleteCase = (caseId) =>
  api.delete(`/admin/cases/${caseId}`, { timeout: 180000 })

export const deleteCases = (caseIds) =>
  api.delete('/admin/cases', { data: { caseIds }, timeout: 180000 })

export const shareCase = (caseId, email, surgeonName, contactId) =>
  api.post(`/admin/cases/${caseId}/share`, { email, surgeonName, contactId })

export const shareAllContacts = (caseId) =>
  api.post(`/admin/cases/${caseId}/share-all`)

export const regeneratePin = (caseId) =>
  api.post(`/admin/cases/${caseId}/regenerate-pin`)

// ── Admin — Care Team (Case Contacts) ────────────────────────────
export const getCaseContacts = (caseId) =>
  api.get(`/admin/cases/${caseId}/contacts`)

export const addCaseContact = (caseId, data) =>
  api.post(`/admin/cases/${caseId}/contacts`, data)

export const updateCaseContact = (id, data) =>
  api.patch(`/admin/contacts/${id}`, data)

export const deleteCaseContact = (id) =>
  api.delete(`/admin/contacts/${id}`)

// ── Admin — Uploads ──────────────────────────────────────────────
export const getUploadsForCase = (caseId) =>
  api.get(`/admin/cases/${caseId}/uploads`)

export const getValidationReport = (uploadId) =>
  api.get(`/admin/uploads/${uploadId}/report`)

export const validateUpload = (uploadId) =>
  api.post(`/admin/uploads/${uploadId}/validate`)

// ── Admin — Forma (multi-anatomy scan cases) ─────────────────────
export const getCranialCases = () =>
  api.get('/admin/cranial/cases')

export const createCranialCase = (caseId, notes, scanType, scanTypeLabel) =>
  api.post('/admin/cranial/cases', {
    ...(caseId ? { caseId } : {}),
    ...(notes ? { notes } : {}),
    ...(scanType ? { scanType } : {}),
    ...(scanTypeLabel ? { scanTypeLabel } : {}),
  })

export const regenerateCranialPin = (caseId) =>
  api.post(`/admin/cranial/cases/${caseId}/regenerate-pin`)

export const getCranialScans = (caseId) =>
  api.get(`/admin/cranial/cases/${caseId}/scans`)

export const reinitiateCranialCase = (caseId) =>
  api.post(`/admin/cranial/cases/${caseId}/reinitiate`)

export const setCranialCaseActive = (caseId, isActive) =>
  api.post(`/admin/cranial/cases/${caseId}/deactivate`, { isActive })

export const deleteCranialCase = (caseId) =>
  api.delete(`/admin/cranial/cases/${caseId}`)

// ── Admin — Podiatry (orthotic order workflow, FEET cases) ───────
export const getPodiatryCase = (caseId) =>
  api.get(`/admin/podiatry/${caseId}`)

export const savePodiatryIntake = (caseId, data) =>
  api.put(`/admin/podiatry/${caseId}/intake`, data)

export const savePodiatryMeasurements = (caseId, data) =>
  api.put(`/admin/podiatry/${caseId}/measurements`, data)

export const deletePodiatryPhoto = (photoId) =>
  api.delete(`/admin/podiatry/photo/${photoId}`)

// Streams the photo through the backend (small files). <img> tags can't send the
// auth header, so fetch as a blob and objectURL it in the component.
export const fetchPodiatryPhotoBlob = (photoId) =>
  api.get(`/admin/podiatry/photo/${photoId}`, { responseType: 'blob' })

export const podiatryArtifactsBatchInit = (caseId, files) =>
  api.post(`/admin/podiatry/${caseId}/artifacts/batch-init`, { files })

export const podiatryArtifactsBatchComplete = (caseId, results) =>
  api.post(`/admin/podiatry/${caseId}/artifacts/batch-complete`, { results })

export const deleteUploadSession = (caseId, { sessionFolder, uploadId }) =>
  api.delete(`/admin/cases/${caseId}/uploads/session`, { data: { sessionFolder, uploadId } })

// ── Admin — WhatsApp recipients ──────────────────────────────────
export const getRecipients = () =>
  api.get('/admin/whatsapp/recipients')

export const addRecipient = (data) =>
  api.post('/admin/whatsapp/recipients', data)

export const updateRecipient = (id, data) =>
  api.patch(`/admin/whatsapp/recipients/${id}`, data)

export const removeRecipient = (id) =>
  api.delete(`/admin/whatsapp/recipients/${id}`)

export const deleteRecipientPermanent = (id) =>
  api.delete(`/admin/whatsapp/recipients/${id}/permanent`)

export const sendTestMessage = (recipientId) =>
  api.post(`/admin/whatsapp/recipients/${recipientId}/test`)

// ── Admin — Metrics ───────────────────────────────────────────────
export const getMetrics = () =>
  api.get('/admin/metrics')

// ── Admin — Queue ─────────────────────────────────────────────────
export const getQueueStats = () =>
  api.get('/admin/queue/stats')

export const retryFailedJobs = () =>
  api.post('/admin/queue/retry-failed')

export const cleanQueue = () =>
  api.post('/admin/queue/clean')

export const getQueueJobs = (state = 'failed') =>
  api.get('/admin/queue/jobs', { params: { state } })

// ── Admin — Drive health ──────────────────────────────────────────
export const getDriveStatus = () =>
  api.get('/admin/drive/status')

// ── Admin — Case purge ────────────────────────────────────────────
export const purgeCase = (caseId) =>
  api.delete(`/admin/cases/${caseId}/purge`)

// ── Admin — DICOM Quality Check ──────────────────────────────────
// 120s timeout: the FIRST (uncached) compute range-reads a large ZIP and can exceed
// the default 30s (worse on a cold instance); once cached on Drive it's sub-second.
export const getDicomQuality = (caseId, folderId) =>
  api.get(`/admin/cases/${caseId}/quality/${folderId}`, { timeout: 120000 })

export const getCaseDicomMeta = (caseId, uploadId) =>
  api.get(`/admin/cases/${caseId}/dicom-meta`, { params: { uploadId }, timeout: 120000 })

export const backfillQuality = () =>
  api.post('/admin/backfill-quality')

// ── Payment (public) ──────────────────────────────────────────────
export const getPaymentInfo = (caseId) =>
  api.get(`/payment/${caseId}`)

export const createPaymentOrder = (caseId, data) =>
  api.post(`/payment/${caseId}/order`, data)

export const verifyPayment = (data) =>
  api.post('/payment/verify', data)

// ── Admin — Payments ──────────────────────────────────────────────
export const setPaymentAmount = (caseId, amount, description) =>
  api.patch(`/admin/cases/${caseId}/payment`, { amount, description })

export const waivePayment = (caseId) =>
  api.patch(`/admin/cases/${caseId}/payment/waive`)

export const getPaymentHistory = (caseId) =>
  api.get(`/admin/cases/${caseId}/payments`)

// ── Admin — Models (GLB/STL) ─────────────────────────────────────
export const getModelsForCase = (caseId) =>
  api.get(`/models/${caseId}`)

const CHUNK_SIZE = 20 * 1024 * 1024 // 20 MB — must match backend

export const uploadModel = async (caseId, file, description, onProgress) => {
  // Small files (≤ 25 MB): single-request upload
  if (file.size <= 25 * 1024 * 1024) {
    const formData = new FormData()
    formData.append('model', file)
    if (description) formData.append('description', description)
    return api.post(`/models/${caseId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
      onUploadProgress: onProgress
        ? (e) => onProgress(e.loaded, e.total)
        : undefined,
    })
  }

  // Large files: chunked upload (bypasses Cloud Run 32 MiB limit)
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE)

  // Step 1: Initiate chunked upload session
  const { data: { uploadId } } = await api.post(`/models/${caseId}/upload/init`, {
    filename: file.name,
    totalChunks,
    fileSize: file.size,
    description: description || '',
  })

  // Step 2: Send each chunk sequentially with smooth byte-level progress.
  // The last chunk's response blocks on backend STL→GLB conversion — we emit
  // `loaded === total` as soon as bytes are flushed, so callers can show a
  // "processing" state for the gap between 100% sent and HTTP response received.
  let totalSent = 0
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE
    const end = Math.min(start + CHUNK_SIZE, file.size)
    const chunkSize = end - start
    const chunk = file.slice(start, end)
    const chunkStart = totalSent

    const formData = new FormData()
    formData.append('chunk', chunk, `chunk-${i}`)

    const res = await api.post(`/models/upload/chunk/${uploadId}?index=${i}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
      onUploadProgress: (e) => {
        if (onProgress && e.loaded != null) {
          const loaded = Math.min(e.loaded, chunkSize)
          onProgress(chunkStart + loaded, file.size)
        }
      },
    })

    totalSent = chunkStart + chunkSize
    if (onProgress) onProgress(totalSent, file.size)

    // Last chunk returns the created model
    if (res.status === 201) return res
  }
}

export const deleteModel = (modelId) =>
  api.delete(`/models/${modelId}`)

// ── Admin — Annotations (Surgeon Markings) ──────────────────────
export const getAnnotations = (params) =>
  api.get('/admin/annotations', { params })

export const updateAnnotationStatus = (annotationId, status) =>
  api.patch(`/models/annotation/${annotationId}`, { status })

export const requestReview = (caseId, data) =>
  api.post(`/models/${caseId}/request-review`, data)

export const getCaseAnnotations = (caseId) =>
  api.get(`/models/${caseId}/annotations`)

export const getAnnotationFileUrl = (annotationId) =>
  `${api.defaults.baseURL}/models/annotation/${annotationId}/file`

// ── Admin — Orphan Uploads ───────────────────────────────────────
export const getOrphanUploads = () =>
  api.get('/admin/uploads/orphans')

export const purgeAllOrphans = () =>
  api.delete('/admin/uploads/orphans')

export const deleteOrphan = (id) =>
  api.delete(`/admin/uploads/orphans/${id}`)

// ── Planning Calendar ────────────────────────────────────────────
export const getPlanCalendar = (from, to) =>
  api.get(`/plan/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)

export const getPlanAvailability = () =>
  api.get('/plan/availability')

export const addPlanAvailability = (data) =>
  api.post('/plan/availability', data)

export const deletePlanAvailability = (id) =>
  api.delete(`/plan/availability/${id}`)

// ── Case Meetings (Google Meet lifecycle) ───────────────────────
export const createMeeting = (caseId, data) =>
  api.post(`/admin/cases/${caseId}/meetings`, data)

export const getAllMeetings = (params) =>
  api.get('/admin/meetings', { params })

export const getCaseMeetings = (caseId) =>
  api.get(`/admin/cases/${caseId}/meetings`)

export const getMeeting = (meetingId) =>
  api.get(`/admin/meetings/${meetingId}`)

export const rescheduleMeeting = (meetingId, data) =>
  api.patch(`/admin/meetings/${meetingId}/reschedule`, data)

export const cancelMeeting = (meetingId, data) =>
  api.post(`/admin/meetings/${meetingId}/cancel`, data)

export const deleteMeeting = (meetingId) =>
  api.delete(`/admin/meetings/${meetingId}`)

export const bulkDeleteMeetings = (view) =>
  api.delete('/admin/meetings', { params: { view } })

export const getDriveConnectionStatus = () =>
  api.get('/admin/auth/drive-status')

export const getCalendarStatus = () =>
  api.get('/admin/auth/calendar-status')

// ── Admin User Management ────────────────────────────────────────
export const getAdminUsers = () =>
  api.get('/admin/users')

export const addAdminUser = (data) =>
  api.post('/admin/users', data)

export const removeAdminUser = (id) =>
  api.delete(`/admin/users/${id}`)

// ── Admin — Licenses ────────────────────────────────────────────
export const getLicenseProducts = () =>
  api.get('/licenses/admin/products')

export const getLicenses = (params) =>
  api.get('/licenses/admin/licenses', { params })

export const issueLicense = (data) =>
  api.post('/licenses/admin/licenses', data)

export const getLicenseDetail = (id) =>
  api.get(`/licenses/admin/licenses/${id}`)

export const updateLicense = (id, data) =>
  api.patch(`/licenses/admin/licenses/${id}`, data)

export const revokeLicense = (id, reason) =>
  api.post(`/licenses/admin/licenses/${id}/revoke`, { reason })

export const renewLicense = (id, newExpiresAt) =>
  api.post(`/licenses/admin/licenses/${id}/renew`, { newExpiresAt })

export const getLicenseActivations = (id, params) =>
  api.get(`/licenses/admin/licenses/${id}/activations`, { params })

export const getLicenseStats = () =>
  api.get('/licenses/admin/stats')

export const deleteLicense = (id) =>
  api.delete(`/licenses/admin/licenses/${id}`)

export const bulkPurgeLicenses = (params) =>
  api.delete('/licenses/admin/licenses', { params })

// ── SurgConnect admin ──────────────────────────────────────────────
export const scGetDoctors   = (params) => api.get('/surg-connect/admin/doctors', { params })
export const scVerifyDoctor = (id, body) => api.patch(`/surg-connect/admin/doctors/${id}/verify`, body)
export const scDeleteDoctor = (id)       => api.delete(`/surg-connect/admin/doctors/${id}`)
export const scGetStats     = ()         => api.get('/surg-connect/admin/stats')

export default api

// ── Admin-driven updates, per product ──────────────────────────────
//
// 🔴 The SSE path is PERMANENT and must keep working: it is compiled into every shipped SSE
// binary, so it can never move. `/api/sse/...` and `/api/p/sse/...` reach the same handlers and
// resolve the same product; SSE's own URL simply stays as it is. Every other product goes
// through /api/p/<slug>/, which is what its binaries are built against.
const updatesBase = (productSlug = 'sse') =>
  (productSlug === 'sse' ? '/sse' : `/p/${productSlug}`)

export const getSseReleases        = (p = 'sse') => api.get(`${updatesBase(p)}/updates/releases`)
export const registerSseRelease    = (body, p = 'sse') => api.post(`${updatesBase(p)}/updates/releases`, { ...body, productSlug: p })
export const getSseWorkstations    = (p = 'sse') => api.get(`${updatesBase(p)}/updates/workstations`)
export const setSseWorkstation     = (deviceCode, body, p = 'sse') => api.post(`${updatesBase(p)}/updates/workstations/${deviceCode}`, body)
export const setSseRollout         = (defaultVersion, p = 'sse') => api.post(`${updatesBase(p)}/updates/rollout`, { defaultVersion })

// Creating a licensed product was API-only — there was no form, which is why SPE could not be
// activated for a day after its licence gate shipped.
export const createLicenseProduct  = (body) => api.post('/licenses/admin/products', body)
